/**
 * Formatting helpers shared by every readout.
 *
 * The game deals in numbers spanning seven orders of magnitude (a level-1 mine
 * holds hundreds of Titan; a Dyson sphere costs hundreds of millions), so the
 * HUD compacts anything past five digits rather than letting the resource bar
 * reflow.
 */

const COMPACT_UNITS = ['', 'K', 'M', 'Mrd', 'Bio'] as const;

/**
 * Compact a resource amount to ~3 significant digits: 82_100 → "82.1K",
 * 124_500 → "125K", 82_100_000 → "82.1M". Values below `threshold` are shown
 * in full with thousands separators, because early-game players need exact
 * counts to plan a build.
 */
export function formatAmount(
  value: number,
  options: { threshold?: number; locale?: string; decimals?: number } = {},
): string {
  const { threshold = 100_000, locale = 'de-DE', decimals = 1 } = options;
  if (!Number.isFinite(value)) return '—';

  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);

  if (abs < threshold) {
    return sign + Math.floor(abs).toLocaleString(locale);
  }

  let unitIndex = 0;
  let scaled = abs;
  while (scaled >= 1000 && unitIndex < COMPACT_UNITS.length - 1) {
    scaled /= 1000;
    unitIndex += 1;
  }

  const precision = scaled >= 100 ? 0 : decimals;
  return `${sign}${scaled.toFixed(precision)}${COMPACT_UNITS[unitIndex]}`;
}

/** Full-precision amount with thousands separators — for tooltips and cost breakdowns. */
export function formatExact(value: number, locale = 'de-DE'): string {
  if (!Number.isFinite(value)) return '—';
  return Math.floor(value).toLocaleString(locale);
}

/**
 * Countdown formatting: 252 → "00:04:12", 90_000 → "1d 01:00:00".
 * Always zero-padded so the digits sit still while ticking.
 */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00:00';
  const total = Math.floor(seconds);
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  const clock = `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
  return days > 0 ? `${days}d ${clock}` : clock;
}

/**
 * Short duration for dense rows: "4h 12m", "12m 30s", "40s".
 *
 * A zero trailing unit is dropped rather than printed — "13h 0m" and "4m 0s"
 * are noise, and build times land on round hours often enough that the padding
 * would show up all over the buildings grid.
 */
export function formatDurationShort(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0s';
  const total = Math.floor(seconds);
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  const join = (major: string, minor: string, minorValue: number) =>
    minorValue > 0 ? `${major} ${minor}` : major;

  if (days > 0) return join(`${days}d`, `${hours}h`, hours);
  if (hours > 0) return join(`${hours}h`, `${minutes}m`, minutes);
  if (minutes > 0) return join(`${minutes}m`, `${secs}s`, secs);
  return `${secs}s`;
}

/** Signed rate for production readouts: 1240 → "+1.240/h". */
export function formatRate(perHour: number, locale = 'de-DE'): string {
  const sign = perHour > 0 ? '+' : perHour < 0 ? '−' : '';
  return `${sign}${Math.abs(Math.round(perHour)).toLocaleString(locale)}/h`;
}

/** Clamp a 0..1 ratio, tolerating bad server data. */
export function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/** Percentage string for a 0..1 ratio. */
export function formatPercent(ratio: number, decimals = 0): string {
  return `${(clamp01(ratio) * 100).toFixed(decimals)}%`;
}

/** Galactic coordinates: "[4:186:9]" — the canonical target notation. */
export function formatCoordinates(ring: number, system: number, position: number): string {
  return `[${ring}:${system}:${position}]`;
}
